<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import DatePicker from 'primevue/datepicker';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import RoleBadge from '@/components/RoleBadge.vue';
import type {
  BonusLootTierInput,
  EditableQuestScene,
  PlatformEvent,
  PlaygroundUserScores,
  QuestAdminConfig,
  QuestSceneConfig,
  RoleDefinition,
  UserProfile,
} from '@/types/api';
import {
  advanceQuestScene,
  createEvent,
  deleteEvent,
  divineQuest,
  failQuest,
  fetchAdminEvents,
  fetchAdminPlaygroundScores,
  fetchAdminBonusLootTiers,
  fetchAdminUsers,
  fetchQuestAdminConfig,
  fetchRoles,
  patchQuestAdminConfig,
  patchQuestScene,
  startQuest,
  saveAdminBonusLootTiers,
  updateAdminUserScores,
  updateRoleLabel,
  updateUserRole,
} from '@/lib/apiClient';
import { formatDateKeyRu, toDateKey } from '@/lib/dateKey';
import { useAuth } from '@/composables/useAuth';

const toast = useToast();
const { token } = useAuth();

const loading = ref(true);
const savingEvent = ref(false);
const deletingEventId = ref<number | null>(null);
const savingRoleKey = ref<string | null>(null);
const savingScoresUserId = ref<number | null>(null);
const questSaving = ref(false);
const questStarting = ref(false);
const users = ref<UserProfile[]>([]);
const roles = ref<RoleDefinition[]>([]);
const events = ref<PlatformEvent[]>([]);
const playgroundScores = ref<PlaygroundUserScores[]>([]);
const questConfig = ref<QuestAdminConfig | null>(null);
const questScenes = ref<EditableQuestScene[]>([]);
const questActive = ref(false);
const divineBossDamage = ref(30);
const bonusLootDraft = ref<BonusLootTierInput[]>([]);
const savingBonusLoot = ref(false);

function formatRiddleAnswers(raw: string | null): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map(String).join(', ');
    }
  } catch {
    return raw;
  }
  return '';
}

function toEditableScenes(scenes: QuestSceneConfig[]): EditableQuestScene[] {
  return scenes.map((scene) => ({
    ...scene,
    diceMax: scene.diceMax ?? 12,
    diceThreshold: scene.diceThreshold ?? 7,
    riddleAnswersText: formatRiddleAnswers(scene.riddleAnswers),
  }));
}

const eventTitle = ref('');
const eventDescription = ref('');
const eventDate = ref<Date | null>(new Date());
const eventEmoji = ref('🎂');

const roleOptions = computed(() =>
  roles.value.map((role) => ({ label: role.label, value: role.key })),
);

async function loadData() {
  if (!token.value) return;

  const [usersResponse, rolesResponse, eventsResponse, scoresResponse, questResponse, bonusLootResponse] =
    await Promise.all([
    fetchAdminUsers(token.value),
    fetchRoles(token.value),
    fetchAdminEvents(token.value),
    fetchAdminPlaygroundScores(token.value),
    fetchQuestAdminConfig(token.value).catch(() => null),
    fetchAdminBonusLootTiers(token.value).catch(() => null),
  ]);

  users.value = usersResponse.users;
  roles.value = rolesResponse.roles;
  events.value = eventsResponse.events;
  playgroundScores.value = scoresResponse.scores;

  if (questResponse) {
    questConfig.value = {
      ...questResponse.config,
      weaponDoubleGoldCost: questResponse.config.weaponDoubleGoldCost ?? 25,
      weaponCritGoldCost: questResponse.config.weaponCritGoldCost ?? 25,
      weaponTimeGoldCost: questResponse.config.weaponTimeGoldCost ?? 20,
    };
    questScenes.value = toEditableScenes(questResponse.scenes);
    questActive.value = Boolean(questResponse.activeRun);
  }

  if (bonusLootResponse) {
    bonusLootDraft.value = bonusLootResponse.tiers.map((tier) => ({
      rewardAmount: tier.rewardAmount,
      chancePercent: tier.chancePercent,
    }));
  } else {
    bonusLootDraft.value = [
      { rewardAmount: 1000, chancePercent: 1 },
      { rewardAmount: 500, chancePercent: 10 },
      { rewardAmount: 300, chancePercent: 30 },
      { rewardAmount: 50, chancePercent: 59 },
    ];
  }
}

onMounted(async () => {
  if (!token.value) {
    loading.value = false;
    return;
  }

  try {
    await loadData();
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Не удалось загрузить данные',
      detail: error instanceof Error ? error.message : 'Ошибка API',
      life: 4000,
    });
  } finally {
    loading.value = false;
  }
});

async function submitEvent() {
  if (!token.value || !eventDate.value) return;
  savingEvent.value = true;

  try {
    await createEvent(token.value, {
      title: eventTitle.value,
      description: eventDescription.value,
      eventDate: toDateKey(eventDate.value),
      emoji: eventEmoji.value,
    });
    eventTitle.value = '';
    eventDescription.value = '';
    events.value = (await fetchAdminEvents(token.value)).events;
    toast.add({ severity: 'success', summary: 'Событие создано', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось создать',
      life: 3000,
    });
  } finally {
    savingEvent.value = false;
  }
}

async function removeEvent(eventId: number) {
  if (!token.value) return;
  deletingEventId.value = eventId;

  try {
    await deleteEvent(token.value, eventId);
    events.value = events.value.filter((event) => event.id !== eventId);
    toast.add({ severity: 'success', summary: 'Событие удалено', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось удалить',
      life: 3000,
    });
  } finally {
    deletingEventId.value = null;
  }
}

async function changeRole(userId: number, role: string) {
  if (!token.value) return;

  try {
    const result = await updateUserRole(token.value, userId, role);
    users.value = users.value.map((user) => (user.id === userId ? result.user : user));
    toast.add({ severity: 'success', summary: 'Роль обновлена', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось обновить роль',
      life: 3000,
    });
  }
}

async function saveRoleMeta(role: RoleDefinition) {
  if (!token.value) return;
  savingRoleKey.value = role.key;

  try {
    const result = await updateRoleLabel(token.value, role.key, {
      label: role.label,
      badgeColor: role.badgeColor,
    });
    roles.value = roles.value.map((item) => (item.key === role.key ? result.role : item));
    toast.add({ severity: 'success', summary: 'Справочник обновлён', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сохранить',
      life: 3000,
    });
  } finally {
    savingRoleKey.value = null;
  }
}

async function savePlaygroundScores(row: PlaygroundUserScores) {
  if (!token.value) return;
  savingScoresUserId.value = row.userId;

  try {
    const result = await updateAdminUserScores(token.value, row.userId, {
      money: row.money,
      health: row.health,
      love: row.love,
    });
    playgroundScores.value = playgroundScores.value.map((item) =>
      item.userId === row.userId
        ? {
            ...item,
            money: result.scores.money,
            health: result.scores.health,
            love: result.scores.love,
            total: result.scores.total,
          }
        : item,
    );
    toast.add({ severity: 'success', summary: 'Очки сохранены', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сохранить',
      life: 3000,
    });
  } finally {
    savingScoresUserId.value = null;
  }
}

function addBonusLootRow() {
  bonusLootDraft.value.push({ rewardAmount: 100, chancePercent: 5 });
}

function removeBonusLootRow(index: number) {
  bonusLootDraft.value.splice(index, 1);
}

const bonusLootChanceTotal = computed(() =>
  bonusLootDraft.value.reduce((sum, tier) => sum + Number(tier.chancePercent || 0), 0),
);

async function saveBonusLoot() {
  if (!token.value) return;
  savingBonusLoot.value = true;
  try {
    const result = await saveAdminBonusLootTiers(token.value, bonusLootDraft.value);
    bonusLootDraft.value = result.tiers.map((tier) => ({
      rewardAmount: tier.rewardAmount,
      chancePercent: tier.chancePercent,
    }));
    toast.add({ severity: 'success', summary: 'Таблица бонусов сохранена', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сохранить',
      life: 3000,
    });
  } finally {
    savingBonusLoot.value = false;
  }
}

async function saveQuestConfig() {
  if (!token.value || !questConfig.value) return;
  questSaving.value = true;
  try {
    const result = await patchQuestAdminConfig(token.value, {
      finalNote: questConfig.value.finalNote,
      durationMinutes: questConfig.value.durationMinutes,
      tickMinutes: questConfig.value.tickMinutes,
      bossMaxHp: questConfig.value.bossMaxHp,
      bossPhase2Hp: questConfig.value.bossPhase2Hp,
      weaponDoubleGoldCost: questConfig.value.weaponDoubleGoldCost,
      weaponCritGoldCost: questConfig.value.weaponCritGoldCost,
      weaponTimeGoldCost: questConfig.value.weaponTimeGoldCost,
    });
    questConfig.value = result.config;
    questScenes.value = toEditableScenes(result.scenes);
    toast.add({ severity: 'success', summary: 'Настройки квеста сохранены', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сохранить',
      life: 3000,
    });
  } finally {
    questSaving.value = false;
  }
}

async function saveQuestScene(scene: EditableQuestScene) {
  if (!token.value) return;
  questSaving.value = true;
  try {
    const result = await patchQuestScene(token.value, scene.sceneIndex, {
      hpCost: scene.hpCost,
      goldCost: scene.goldCost,
      loveCost: scene.loveCost,
      narrative: scene.narrative,
    });
    questScenes.value = toEditableScenes(result.scenes);
    toast.add({ severity: 'success', summary: `Сцена «${scene.title}» сохранена`, life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось сохранить',
      life: 3000,
    });
  } finally {
    questSaving.value = false;
  }
}

async function handleStartQuest() {
  if (!token.value) return;
  questStarting.value = true;
  try {
    await startQuest(token.value);
    questActive.value = true;
    toast.add({ severity: 'success', summary: 'Квест запущен!', life: 3000 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Старт',
      detail: error instanceof Error ? error.message : 'Не удалось запустить',
      life: 3500,
    });
  } finally {
    questStarting.value = false;
  }
}

async function handleFailQuest() {
  if (!token.value) return;
  try {
    await failQuest(token.value);
    questActive.value = false;
    toast.add({ severity: 'warn', summary: 'Квест остановлен', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось',
      life: 3000,
    });
  }
}

async function handleDivine(action: string, amount = 30) {
  if (!token.value) return;
  try {
    await divineQuest(token.value, action, amount);
    toast.add({ severity: 'success', summary: 'Божественное вмешательство', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось',
      life: 3000,
    });
  }
}

async function handleAdvanceScene() {
  if (!token.value) return;
  try {
    await advanceQuestScene(token.value);
    toast.add({ severity: 'info', summary: 'Сцена пропущена (тест)', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось',
      life: 3000,
    });
  }
}
</script>

<template>
  <div class="page">
    <section class="page__hero">
      <h1>Админ-панель</h1>
      <p>События, роли, «Три Желания», квест «Лес Трёх Желаний» и справочник плашек.</p>
    </section>

    <ProgressSpinner v-if="loading" />

    <template v-else>
      <Card>
        <template #title>Новое событие</template>
        <template #content>
          <label class="field">
            <span>Название</span>
            <InputText v-model="eventTitle" placeholder="День рождения" fluid />
          </label>
          <label class="field">
            <span>Описание</span>
            <Textarea v-model="eventDescription" rows="3" auto-resize fluid />
          </label>
          <label class="field">
            <span>Дата</span>
            <DatePicker v-model="eventDate" date-format="dd.mm.yy" show-icon fluid />
          </label>
          <label class="field">
            <span>Emoji</span>
            <InputText v-model="eventEmoji" maxlength="4" fluid />
          </label>
        </template>
        <template #footer>
          <Button label="Создать событие" icon="pi pi-plus" :loading="savingEvent" @click="submitEvent" />
        </template>
      </Card>

      <Card>
        <template #title>Мероприятия</template>
        <template #content>
          <p v-if="events.length === 0" class="empty">Событий пока нет.</p>
          <div v-else class="events">
            <div v-for="event in events" :key="event.id" class="events__row">
              <div class="events__info">
                <span class="events__emoji">{{ event.emoji }}</span>
                <div>
                  <strong>{{ event.title }}</strong>
                  <small>
                    {{ formatDateKeyRu(event.eventDate, { day: 'numeric', month: 'long', year: 'numeric' }) }}
                  </small>
                  <p v-if="event.description" class="events__desc">{{ event.description }}</p>
                </div>
              </div>
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                :loading="deletingEventId === event.id"
                @click="removeEvent(event.id)"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card>
        <template #title>Три Желания — очки участников</template>
        <template #subtitle>Задайте любые значения для денег, здоровья и любви</template>
        <template #content>
          <div class="scores">
            <div v-for="row in playgroundScores" :key="row.userId" class="scores__row">
              <div class="scores__user">
                <UserAvatar :nickname="row.nickname" :avatar="row.avatar" />
                <strong>{{ row.nickname }}</strong>
              </div>
              <label class="scores__field">
                <span>💰</span>
                <InputNumber v-model="row.money" :min="0" :max="9999999" fluid />
              </label>
              <label class="scores__field">
                <span>💚</span>
                <InputNumber v-model="row.health" :min="0" :max="9999999" fluid />
              </label>
              <label class="scores__field">
                <span>❤️</span>
                <InputNumber v-model="row.love" :min="0" :max="9999999" fluid />
              </label>
              <div class="scores__total">Σ {{ row.money + row.health + row.love }}</div>
              <Button
                label="Сохранить"
                size="small"
                :loading="savingScoresUserId === row.userId"
                @click="savePlaygroundScores(row)"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card>
        <template #title>✨ Бонусные цели (кликер и босс)</template>
        <template #subtitle>
          Таблица выпадения при попадании по эмодзи. Сумма шансов может быть меньше 100% — остаток это «мимо».
        </template>
        <template #content>
          <div class="bonus-loot">
            <div class="bonus-loot__head">
              <span>Награда</span>
              <span>Шанс, %</span>
              <span></span>
            </div>
            <div v-for="(tier, index) in bonusLootDraft" :key="`bonus-row-${index}`" class="bonus-loot__row">
              <InputNumber v-model="tier.rewardAmount" :min="1" :max="1000000" :use-grouping="false" fluid />
              <InputNumber
                v-model="tier.chancePercent"
                :min="0.01"
                :max="100"
                :min-fraction-digits="0"
                :max-fraction-digits="2"
                :use-grouping="false"
                fluid
              />
              <Button icon="pi pi-trash" severity="danger" text rounded @click="removeBonusLootRow(index)" />
            </div>
            <p class="quest-hint">
              Сумма шансов: {{ bonusLootChanceTotal.toFixed(2) }}%
              <template v-if="bonusLootChanceTotal < 100">
                · мимо: {{ (100 - bonusLootChanceTotal).toFixed(2) }}%
              </template>
            </p>
            <div class="bonus-loot__actions">
              <Button label="Добавить строку" icon="pi pi-plus" outlined size="small" @click="addBonusLootRow" />
              <Button label="Сохранить таблицу" icon="pi pi-save" :loading="savingBonusLoot" @click="saveBonusLoot" />
            </div>
          </div>
        </template>
      </Card>

      <Card v-if="questConfig">
        <template #title>🌲 Лес Трёх Желаний</template>
        <template #subtitle>
          RPG-кампания. Божественный Создатель открывает портал; магический отряд проходит главы.
          Новые герои могут выйти из тумана в любой момент — лес запишет это в хронику.
        </template>
        <template #content>
          <div class="quest-actions">
            <Button
              label="Открыть портал леса"
              icon="pi pi-play"
              severity="success"
              :loading="questStarting"
              :disabled="questActive"
              @click="handleStartQuest"
            />
            <Button
              label="Закрыть портал"
              icon="pi pi-times"
              severity="danger"
              outlined
              :disabled="!questActive"
              @click="handleFailQuest"
            />
            <Button
              label="Следующая глава (тест)"
              icon="pi pi-forward"
              severity="secondary"
              outlined
              :disabled="!questActive"
              @click="handleAdvanceScene"
            />
          </div>

          <div class="quest-divine">
            <span>Вмешательство Создателя:</span>
            <Button label="+30 всем" size="small" outlined :disabled="!questActive" @click="handleDivine('buff')" />
            <Button label="−50% цен" size="small" outlined :disabled="!questActive" @click="handleDivine('reduce_costs')" />
            <span class="quest-divine__boss">
              <InputNumber
                v-model="divineBossDamage"
                :min="1"
                :max="100000"
                :use-grouping="false"
                :disabled="!questActive"
                show-buttons
                button-layout="horizontal"
                class="quest-divine__boss-input"
              />
              <Button
                :label="`−${divineBossDamage ?? 0} HP боссу`"
                size="small"
                outlined
                :disabled="!questActive || !divineBossDamage"
                @click="handleDivine('boss_damage', divineBossDamage ?? 0)"
              />
            </span>
            <Button label="+30 в котёл" size="small" outlined :disabled="!questActive" @click="handleDivine('fill_pool')" />
          </div>

          <label class="field">
            <span>Финальная записка при победе</span>
            <Textarea v-model="questConfig.finalNote" rows="2" auto-resize fluid />
          </label>
          <div class="quest-config-row">
            <label class="field">
              <span>👹 HP Хранителя</span>
              <InputNumber v-model="questConfig.bossMaxHp" :min="50" :max="100000" fluid />
            </label>
            <label class="field">
              <span>Порог фазы II (HP)</span>
              <InputNumber v-model="questConfig.bossPhase2Hp" :min="0" :max="100000" fluid />
            </label>
          </div>
          <p class="quest-hint quest-hint--section">🛡️ Стоимость оружия в лавке (золото)</p>
          <div class="quest-config-row">
            <label class="field">
              <span>⚔️ Удвоитель (×2)</span>
              <InputNumber v-model="questConfig.weaponDoubleGoldCost" :min="0" :max="10000" fluid />
            </label>
            <label class="field">
              <span>💥 Критический клинок</span>
              <InputNumber v-model="questConfig.weaponCritGoldCost" :min="0" :max="10000" fluid />
            </label>
            <label class="field">
              <span>⏳ Песочные часы</span>
              <InputNumber v-model="questConfig.weaponTimeGoldCost" :min="0" :max="10000" fluid />
            </label>
          </div>
          <p class="quest-hint">
            Финальная победа — когда HP Хранителя опустится до 0. Перед битвой — лавка с тремя видами
            оружия. Очередь атаки выстраивает именинник или админ; каждый герой кликает по боссу 1–2 минуты.
            Не хватает очков — докликивайте в «Три Желания» и забирайте в квест.
          </p>
          <Button label="Сохранить настройки" :loading="questSaving" @click="saveQuestConfig" />

          <details class="quest-scenes" open>
            <summary>Экономика сцен ({{ questScenes.length }})</summary>
            <div v-for="scene in questScenes" :key="scene.sceneIndex" class="quest-scene">
              <div class="quest-scene__title-row">
                <strong>{{ scene.sceneIndex }}. {{ scene.title }}</strong>
                <div class="quest-scene__tags">
                  <Tag :value="scene.sceneType" rounded severity="secondary" />
                </div>
              </div>
              <div class="quest-scene__costs">
                <label>💚 <InputNumber v-model="scene.hpCost" :min="0" fluid /></label>
                <label>💰 <InputNumber v-model="scene.goldCost" :min="0" fluid /></label>
                <label>❤️ <InputNumber v-model="scene.loveCost" :min="0" fluid /></label>
              </div>
              <Textarea v-model="scene.narrative" rows="2" auto-resize fluid />

              <Button label="Сохранить сцену" size="small" :loading="questSaving" @click="saveQuestScene(scene)" />
            </div>
          </details>
        </template>
      </Card>

      <Card>
        <template #title>Справочник ролей</template>
        <template #content>
          <div class="roles">
            <div v-for="role in roles" :key="role.key" class="roles__row">
              <code>{{ role.key }}</code>
              <InputText v-model="role.label" fluid />
              <InputText v-model="role.badgeColor" placeholder="#db2777" fluid />
              <RoleBadge :label="role.label" :color="role.badgeColor" />
              <Button
                label="Сохранить"
                size="small"
                :loading="savingRoleKey === role.key"
                @click="saveRoleMeta(role)"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card>
        <template #title>Роли пользователей</template>
        <template #content>
          <div class="users">
            <div v-for="user in users" :key="user.id" class="users__row">
              <UserAvatar :nickname="user.nickname" :avatar="user.avatar" />
              <div class="users__meta">
                <strong>{{ user.nickname }}</strong>
                <RoleBadge
                  v-if="user.roleLabel"
                  :label="user.roleLabel"
                  :color="user.roleBadgeColor"
                />
              </div>
              <Select
                :model-value="user.role"
                :options="roleOptions"
                option-label="label"
                option-value="value"
                @update:model-value="changeRole(user.id, $event)"
              />
            </div>
          </div>
        </template>
      </Card>
    </template>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.page__hero h1 {
  margin: 0 0 0.35rem;
  color: #831843;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #831843;
  font-weight: 600;
}

.empty {
  margin: 0;
  color: #9d174d;
}

.events {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.events__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 1rem;
  background: #fff7fb;
  border: 1px solid #fbcfe8;
}

.events__info {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.events__emoji {
  font-size: 1.6rem;
}

.events__info strong {
  display: block;
  color: #831843;
}

.events__info small {
  color: #be185d;
}

.events__desc {
  margin: 0.35rem 0 0;
  color: #9d174d;
}

.scores {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.scores__row {
  display: grid;
  grid-template-columns: minmax(140px, 1.2fr) repeat(3, minmax(90px, 1fr)) auto auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.85rem;
  border-radius: 1rem;
  background: #fff7fb;
  border: 1px solid #fbcfe8;
}

.scores__user {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.scores__user strong {
  color: #831843;
}

.scores__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.scores__field span {
  font-size: 1.1rem;
}

.scores__total {
  font-weight: 800;
  color: #db2777;
  white-space: nowrap;
}

.roles {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.roles__row {
  display: grid;
  grid-template-columns: 140px 1fr 140px auto auto;
  gap: 0.75rem;
  align-items: center;
}

.roles__row code {
  color: #9d174d;
}

.users {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.users__row {
  display: grid;
  grid-template-columns: auto 1fr 220px;
  gap: 0.75rem;
  align-items: center;
}

.users__meta {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.quest-actions,
.quest-divine {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}

.quest-divine span {
  color: #831843;
  font-weight: 600;
}

.quest-divine__boss {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.quest-divine__boss-input {
  width: 9rem;
}

.quest-divine__boss-input :deep(.p-inputnumber-input) {
  width: 4rem;
  text-align: center;
}

.quest-config-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.quest-hint {
  margin: 0 0 0.85rem;
  font-size: 0.82rem;
  color: #047857;
  line-height: 1.45;
}

.quest-hint--inline {
  margin: 0 0 0.5rem;
}

.quest-hint--section {
  margin-top: 0.75rem;
  font-weight: 600;
}

.bonus-loot {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.bonus-loot__head,
.bonus-loot__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 0.5rem;
  align-items: center;
}

.bonus-loot__head {
  font-size: 0.78rem;
  font-weight: 700;
  color: #065f46;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bonus-loot__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.35rem;
}

@media (max-width: 640px) {
  .bonus-loot__head {
    display: none;
  }

  .bonus-loot__row {
    grid-template-columns: 1fr;
    padding: 0.55rem 0.65rem;
    border: 1px solid #bbf7d0;
    border-radius: 0.75rem;
    background: #f0fdf4;
  }
}

.quest-scenes {
  margin-top: 1.25rem;
}

.quest-scenes summary {
  cursor: pointer;
  color: #831843;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.quest-scene {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
  border-radius: 1rem;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.quest-scene__costs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.quest-scene__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.quest-scene__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.quest-choices {
  margin-top: 0.35rem;
  padding: 0.65rem;
  border-radius: 0.75rem;
  background: #fef9c3;
  border: 1px solid #fde047;
}

.quest-choices summary {
  color: #92400e;
  margin-bottom: 0.5rem;
}

.quest-choices__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 700;
  cursor: pointer;
}

.quest-choice-option {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.65rem;
  margin-bottom: 0.65rem;
  border-radius: 0.65rem;
  background: #fff;
  border: 1px solid #fcd34d;
}

.quest-choice-option__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 900px) {
  .scores__row,
  .roles__row,
  .users__row {
    grid-template-columns: 1fr;
  }
}
</style>
